<!-- this is one flexible component for announcements -->
<!-- alter content AND MODAL_STORAGE_KEY when new announcement is added -->

<template>
  <BaseModal
    v-if="showModal"
    hide-title
    modal-class="common-announcement-modal"
    @close="handleModalDismiss"
  >
    <div class="common-announcement-modal__content">
      <video
        v-if="videoSrc"
        class="common-announcement-modal__image"
        :src="videoSrc"
        :poster="mediaSrc"
        autoplay
        muted
        playsinline
        controls
        aria-label="Wymiana posiłków w aplikacji mobilnej"
      />
      <img
        v-else-if="mediaSrc"
        class="common-announcement-modal__image"
        :src="mediaSrc"
        alt="Wymiana posiłków w aplikacji mobilnej"
      >

      <div class="common-announcement-modal__body">
        <div class="common-announcement-modal__copy">
          <T
            as="h2"
            align="center"
            size="lg"
            type="semibold"
            class="common-announcement-modal__title"
          >
            Nowość: pacjenci sami wymienią posiłek, Ty zachowujesz kontrolę
          </T>

          <div class="common-announcement-modal__description">
            <T size="sm">
              ⚙️&nbsp;Wszystko pod Twoją kontrolą. Funkcja jest domyślnie wyłączona.
              Włącz ją w <strong>Ustawieniach pacjenta</strong> i ustal dla niego zakres wymiany.
            </T>
            <T size="sm">
              💬&nbsp;<strong>Dla Ciebie:</strong> mniej wiadomości z prośbami o drobne zmiany
              w jadłospisie i dłuższa współpraca z pacjentem.
            </T>
            <T size="sm">
              💚&nbsp;<strong>Dla Pacjenta:</strong> łatwiejsze <strong>przestrzeganie założeń diety</strong>
              — jeśli danego dnia coś mu wypadnie, bez obaw sam wymieni posiłek na inny.
            </T>
            <T size="sm">
              Pełną instrukcję, jak korzystać z nowej funkcji znajdziesz w naszej Bazie Wiedzy.
            </T>
          </div>
        </div>

        <div class="common-announcement-modal__actions">
          <StyledButton
            class="common-announcement-modal__close-button"
            size="big"
            variant="tertiary"
            width="short"
            @click.once="handleClose"
          >
            Zamknij
          </StyledButton>

          <StyledButton
            class="common-announcement-modal__learn-more-button"
            :disabled="!learnMoreUrl"
            size="big"
            width="short"
            @click.once="handleLearnMore"
          >
            <GraduationIcon aria-hidden="true" width="20" height="20" />
            Dowiedz się więcej
          </StyledButton>
        </div>
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue'

  import GraduationIcon from '@/icons/meal-exchange-graduation.svg?component'

  import { T } from '@/components/ui/T'
  import { pushDataLayer } from '@/utils/pushDataLayer'

  import BaseModal from './BaseModal.vue'
  import { StyledButton } from './StyledButton'

  const props = defineProps<{
    enabled?: boolean
    userId: number
    videoSrc?: string
    mediaSrc?: string
    learnMoreUrl?: string
  }>()

  const MODAL_STORAGE_KEY = 'meal-exchange-announcement-modal-dismissed'
  const isModalVisible = ref(false)
  const storageKey = computed(() => `${MODAL_STORAGE_KEY}:${props.userId}`)

  const isModalDismissed = () => {
    try {
      return Boolean(localStorage.getItem(storageKey.value))
    } catch {
      return false
    }
  }

  const showModal = computed(() => props.enabled && isModalVisible.value && !isModalDismissed())

  const handleModalDismiss = () => {
    isModalVisible.value = false

    try {
      localStorage.setItem(storageKey.value, 'true')
    } catch {
      // The in-memory state has already hidden the modal for this session.
    }
  }

  const handleClose = () => {
    handleModalDismiss()
    pushDataLayer({
      event: 'new_feature_modal_remind_later',
    })
  }

  const handleLearnMore = () => {
    if (!props.learnMoreUrl) return

    window.open(props.learnMoreUrl, '_blank', 'noopener,noreferrer')
    handleModalDismiss()
    pushDataLayer({
      event: 'new_feature_modal_discover',
    })
  }

  onMounted(() => {
    isModalVisible.value = true
  })
</script>

<style>
  .common-announcement-modal {
    width: min(700px, calc(100vw - 32px));
    padding: 0;
    border-radius: 12px;
    box-shadow: 0 20px 24px -4px rgb(10 13 18 / 8%), 0 8px 8px -4px rgb(10 13 18 / 3%);
  }

  .base-modal-overlay:has(.common-announcement-modal) {
    background-color: rgb(10 13 18 / 70%);
    backdrop-filter: blur(8px);
  }

  .common-announcement-modal__content {
    display: flex;
    flex-direction: row;
    font-family: "Inter Variable", Inter, sans-serif;
    color: #535862;
  }

  .common-announcement-modal__image {
    display: block;
    flex: 0 0 287px;
    width: 287px;
    height: 570px;
    border-right: 1px solid #d5d7da;
    object-fit: cover;
  }

  .common-announcement-modal__body {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
  }

  .common-announcement-modal__copy {
    padding: 40px 32px 0;
  }

  .common-announcement-modal__title {
    margin: 0;
    color: #181d27;
  }

  .common-announcement-modal__description {
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin-top: 40px;
  }

  .common-announcement-modal__description p {
    margin: 0;
    line-height: 20px;
  }

  .common-announcement-modal__actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    padding: 32px 24px 24px;
    margin-top: auto;
  }

  .common-announcement-modal .styled-button {
    flex: 0 0 auto;
    min-width: 0;
    height: 44px;
    padding: 10px 16px;
    font-family: "Inter Variable", Inter, sans-serif;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    border-radius: 8px;
  }

  .common-announcement-modal .styled-button .k-space {
    column-gap: 6px !important;
  }

  .common-announcement-modal .common-announcement-modal__close-button {
    color: #414651;
    background: #fff;
    border: 1px solid #d5d7da;
    box-shadow: 0 1px 2px rgb(10 13 18 / 5%);
  }

  .common-announcement-modal .common-announcement-modal__learn-more-button {
    color: #fff;
    background: #1b412a;
    box-shadow:
      inset 0 0 0 1px rgb(255 255 255 / 12%),
      0 1px 2px rgb(10 13 18 / 5%);
  }

  .common-announcement-modal .common-announcement-modal__learn-more-button:hover:not(:disabled) {
    color: #fff;
    background: #143421;
  }

  @media (width <= 700px) {
    .common-announcement-modal__content {
      flex-direction: column;
    }

    .common-announcement-modal__image {
      flex: none;
      align-self: center;
      width: auto;
      max-width: 100%;
      height: 280px;
      border: 0;
      object-fit: contain;
    }

    .common-announcement-modal__copy {
      padding: 24px 24px 0;
    }

    .common-announcement-modal__description {
      margin-top: 24px;
    }
  }

  @media (width <= 480px) {
    .common-announcement-modal__actions {
      flex-direction: column;
    }

    .common-announcement-modal .styled-button {
      width: 100%;
    }
  }
</style>
